import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AppProvider } from "./context/AppContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import ThemeProvider from "./components/ThemeProvider";
import { Toaster } from "./components/ui/sonner";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import { getRuntimeBasename } from "./utils/runtime";

// Pages
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Pemasukan from "./pages/Pemasukan";
import Pengeluaran from "./pages/Pengeluaran";
import Inventori from "./pages/Inventori";
import Cabang from "./pages/Cabang";
import UserManagement from "./pages/UserManagement";
import SecurityCenter from "./pages/SecurityCenter";
import DatabaseCleaning from "./pages/DatabaseCleaning";
import SiteManager from "./pages/SiteManager";
import BackupManager from "./pages/BackupManager";

// Protected Route wrapper
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Main App Content with auth check
const AppContent = () => {
  const { isAuthenticated, loading, user } = useAuth();
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    if (user?.must_change_password) {
      setShowChangePassword(true);
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Memuat...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <AppProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/pemasukan" element={<Pemasukan />} />
          <Route path="/pengeluaran" element={<Pengeluaran />} />
          <Route path="/inventori" element={<Inventori />} />
          <Route path="/cabang" element={<Cabang />} />
          <Route 
            path="/users" 
            element={
              <ProtectedRoute adminOnly>
                <UserManagement />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/security" 
            element={
              <ProtectedRoute adminOnly>
                <SecurityCenter />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/site-manager" 
            element={
              <ProtectedRoute adminOnly>
                <SiteManager />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/database" 
            element={
              <ProtectedRoute adminOnly>
                <DatabaseCleaning />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/backups" 
            element={
              <ProtectedRoute adminOnly>
                <BackupManager />
              </ProtectedRoute>
            } 
          />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
      
      {/* Force password change dialog */}
      <ChangePasswordDialog 
        open={showChangePassword && user?.must_change_password} 
        onOpenChange={(open) => {
          if (!user?.must_change_password) {
            setShowChangePassword(open);
          }
        }} 
      />
    </AppProvider>
  );
};

function App() {
  return (
    <BrowserRouter basename={getRuntimeBasename()}>
      <ThemeProvider>
        <AuthProvider>
          <AppContent />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
